import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { UPDATE_PROFILE_MUTATION, ME_QUERY, RECOMMENDED_GROUPS_QUERY } from '../graphql';
import { useAuth } from '../context/AuthContext';
import { COUNTRIES_LIST, COUNTRY_PRESETS } from '../i18n';
import { detectLocation as detect } from '../utils/geolocate';

const STORAGE_KEY = 'ho_onboarding_done';

// Coordinates always come back if the device could produce them; the place name is best effort, so
// a failed lookup still fills in what it can rather than discarding the fix.
async function detectLocation(onResult) {
  const { ok, coords, place, error } = await detect();
  if (!ok) { alert(error); return; }
  onResult({
    city: place?.city || '',
    building: place?.building || '',
    lat: coords.lat,
    lng: coords.lng,
  });
  if (!place) alert("Found your position, but couldn't look up the area name — please type your city.");
}

const INTERESTS = ['Reading','Music','Gaming','Art','Cooking','Gardening','Chess','Photography','Dancing','Sports','Crafts','Writing','Science','Programming','Movies','Yoga','Hiking','Drawing','Knitting','Volunteering'];

const PERSONA = {
  KIDS:    { emoji: '🎨', title: 'Welcome!',       subtitle: 'Let\'s find friends who share your hobbies!', color: '#f97316' },
  TEENS:   { emoji: '🎮', title: 'Hey there!',     subtitle: 'Discover groups and people who get you.',     color: '#8b5cf6' },
  ADULTS:  { emoji: '👋', title: 'Welcome aboard!',subtitle: 'Find your tribe and build something great.',  color: '#6366f1' },
  SENIORS: { emoji: '🌿', title: 'Welcome!',       subtitle: 'Connect with neighbours who share your interests.', color: '#10b981' },
};

function StepDots({ total, current }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ width: i === current ? 20 : 8, height: 8, borderRadius: 99, background: i <= current ? 'var(--primary)' : 'var(--border)', transition: 'all 0.3s ease' }} />
      ))}
    </div>
  );
}

export default function OnboardingFlow({ onComplete }) {
  const { currentUser, updateCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [interests, setInterests] = useState(currentUser?.interests || []);
  const [city, setCity] = useState(currentUser?.location?.city || '');
  const [building, setBuilding] = useState(currentUser?.location?.building || '');
  const [country, setCountry] = useState(currentUser?.location?.country || '');
  const [postalCode, setPostalCode] = useState(currentUser?.location?.postalCode || '');

  const countryPreset = COUNTRY_PRESETS[COUNTRIES_LIST.find(c => c.name === country)?.code] || null;
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  const [updateProfile] = useMutation(UPDATE_PROFILE_MUTATION, { refetchQueries: [{ query: ME_QUERY }] });
  const { data: recData } = useQuery(RECOMMENDED_GROUPS_QUERY, { variables: { limit: 3 }, skip: step < 3 });

  const persona = PERSONA[currentUser?.ageGroup] || PERSONA.ADULTS;
  const TOTAL_STEPS = 4;

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1');
    onComplete?.();
  }

  async function saveAndNext() {
    setSaving(true);
    try {
      const vars = {};
      if (step === 1 && interests.length) vars.interests = interests;
      if (step === 2) {
        if (city.trim()) vars.city = city.trim();
        if (building.trim()) vars.building = building.trim();
        if (country.trim()) vars.country = country.trim();
        if (postalCode.trim()) vars.postalCode = postalCode.trim();
        // Auto-set locale/currency/timezone from country
        if (countryPreset) {
          vars.locale = countryPreset.locale;
          vars.currency = countryPreset.currency;
          vars.language = countryPreset.language;
          vars.timezone = countryPreset.timezone;
        }
      }
      if (Object.keys(vars).length) {
        const { data } = await updateProfile({ variables: vars });
        if (data?.updateProfile) updateCurrentUser(data.updateProfile);
      }
    } catch {}
    setSaving(false);
    if (step < TOTAL_STEPS - 1) setStep(s => s + 1);
    else dismiss();
  }

  function toggleInterest(i) {
    setInterests(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
  const modal = { background: 'var(--bg)', borderRadius: 20, padding: '32px 28px', maxWidth: 480, width: '100%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) dismiss(); }}>
      <div style={modal}>
        <button onClick={dismiss} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>

        <StepDots total={TOTAL_STEPS} current={step} />

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>{persona.emoji}</div>
            <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>{persona.title}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>{persona.subtitle}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {[
                { icon: '🎯', text: 'Pick interests to find your best-matched groups' },
                { icon: '📍', text: 'Set your location for hyper-local discovery' },
                { icon: '🤝', text: 'Get smart recommendations + buddy suggestions' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', borderRadius: 10, padding: '10px 14px', textAlign: 'left' }}>
                  <span style={{ fontSize: 20 }}>{item.icon}</span>
                  <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{item.text}</span>
                </div>
              ))}
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setStep(1)}>Let's go →</button>
            <button className="btn btn-ghost" style={{ width: '100%', marginTop: 8 }} onClick={dismiss}>Skip for now</button>
          </div>
        )}

        {/* Step 1: Interests */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>What are your hobbies?</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Pick at least 3 — we'll find your best matches.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {INTERESTS.map(i => (
                <button key={i} onClick={() => toggleInterest(i)}
                  style={{ padding: '7px 14px', borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `2px solid ${interests.includes(i) ? 'var(--primary)' : 'var(--border)'}`, background: interests.includes(i) ? 'rgba(99,102,241,0.12)' : 'var(--surface)', color: interests.includes(i) ? 'var(--primary)' : 'var(--text)' }}>
                  {i}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(0)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 2 }} disabled={interests.length < 1 || saving} onClick={saveAndNext}>
                {saving ? 'Saving…' : `Continue (${interests.length} selected) →`}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Location */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Where are you based?</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Find groups in your building or city — anywhere in the world.</p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Country</label>
              <select className="input" value={country} onChange={e => setCountry(e.target.value)}
                style={{ cursor: 'pointer' }}>
                <option value="">Select your country…</option>
                {COUNTRIES_LIST.map(c => (
                  <option key={c.code} value={c.name}>{COUNTRY_PRESETS[c.code]?.flag || ''} {c.name}</option>
                ))}
              </select>
              {countryPreset && (
                <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 4, fontWeight: 600 }}>
                  ✓ Language, currency &amp; timezone will be set for {country}
                </div>
              )}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>City</label>
              <button type="button" onClick={async()=>{setLocating(true);await detectLocation(({city,building})=>{setCity(city);if(building)setBuilding(building);});setLocating(false);}}
                style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,border:'1.5px solid var(--border)',background:'var(--surface2)',cursor:'pointer',fontWeight:600,fontSize:12,color:'var(--text-muted)',fontFamily:'inherit',marginBottom:10}}>
                {locating ? '⏳ Detecting…' : '📍 Use my current location'}
              </button>
              <input className="input" placeholder={countryPreset?.cityPlaceholder || 'e.g. London'} value={city} onChange={e => setCity(e.target.value)} />
            </div>
            {countryPreset?.postalCodeLabel && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{countryPreset.postalCodeLabel} (optional)</label>
                <input className="input" placeholder={countryPreset.postalCodePlaceholder || ''} value={postalCode} onChange={e => setPostalCode(e.target.value)} />
              </div>
            )}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Building / Estate (optional)</label>
              <input className="input" placeholder={countryPreset?.buildingPlaceholder || 'e.g. Elm Court'} value={building} onChange={e => setBuilding(e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Enables hyper-local group discovery within your building</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 2 }} disabled={saving} onClick={saveAndNext}>
                {saving ? 'Saving…' : 'Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: First recommendations */}
        {step === 3 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Your top picks 🎯</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Based on your interests and location — your best matches.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {(recData?.recommendedGroups || []).slice(0, 3).map(r => (
                <div key={r.group.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', cursor: 'pointer' }}
                  onClick={() => { dismiss(); navigate(`/group/${r.group.id}`); }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{r.group.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{r.group.category} · {r.group.memberCount} members</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {r.matchReasons.map((reason, i) => (
                      <span key={i} style={{ fontSize: 11, padding: '2px 8px', background: 'rgba(99,102,241,0.1)', color: 'var(--primary)', borderRadius: 99, fontWeight: 600 }}>{reason}</span>
                    ))}
                  </div>
                </div>
              ))}
              {(!recData?.recommendedGroups?.length) && (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>Setting up your recommendations…</div>
              )}
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={dismiss}>Explore Ledxo Hub 🚀</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function shouldShowOnboarding(user) {
  if (!user) return false;
  if (localStorage.getItem(STORAGE_KEY)) return false;
  const score = (user.bio?.trim().length > 5 ? 1 : 0) + ((user.interests?.length || 0) >= 2 ? 1 : 0) + (user.location?.city ? 1 : 0);
  return score < 2;
}
